import { all, takeLatest, put, call, select } from 'redux-saga/effects';
import qs from 'query-string';

import cookie from '@snapask/common-components/dist/utils/cookie';

import { USER_LNG } from '@snapask/common-components/dist/constants/cookieNames';
import { SUPPORTED_REGION_LOCALE_LIST } from '@snapask/common-components/dist/constants/locales';

import { getAppsflyerFromCookie } from '@dragonfruit/utils/appsflyers';

import config from '@dragonfruit/config';
const { COOKIE_DOMAIN } = config;

import {
  apiLogin,
  apiAppLaunch,
  apiOauthLogin,
  apiLogout,
} from '@dragonfruit/services/apis';

import i18n from '@dragonfruit/i18n';

import { getFacebookAuthResponse } from '@dragonfruit/sagas/signup/facebook';

import * as actions from '@dragonfruit/redux/auth';
import { fetchSiteInfo } from '@dragonfruit/redux/siteInfo';
import { fetchBankInfoFieldSuccess } from '@dragonfruit/redux/bank';
import {
  storeProfile,
  resetUser,
  getRoleStatus,
  getRole,
  getTermsOfService,
  getRegion as getUserRegion,
} from '@dragonfruit/redux/profile';
import { showAlertBanner } from '@dragonfruit/redux/alertBanner';
import { showAlertModal } from '@dragonfruit/redux/alertModal';
import { actions as errorActions } from '@dragonfruit/redux/error';

import {
  TOKEN_AMOUNT_EXCEED,
  NOW_TV_SUBSCRIPTION_INVALID,
} from '@dragonfruit/constants/errorCode';
import { APPROVED } from '@dragonfruit/constants/tutorStatus';
import {
  ALERT_ERROR_THEME,
  ALERT_SUCCESS_THEME,
} from '@dragonfruit/constants/alertBannerThemes';
import { TUTOR, ADMIN } from '@dragonfruit/constants/userRoles';
import { USER_EMAIL_NOT_VERIFIED } from '@dragonfruit/constants/errorCode';
import { FACEBOOK_AUTH_TYPE } from '@dragonfruit/constants/authType';
import { PHONE, EMAIL } from '@dragonfruit/constants/signupAuthType';

import {
  QUESTION_ID,
  IS_WIZMO_USER,
  PREVIOUS_PURCHASE_PAGE,
} from '@dragonfruit/constants/cookieNames';
import { LOGIN_SUCCESS } from '@dragonfruit/constants/gtmEvents';
import { PERSONAL } from '@dragonfruit/constants/eInvoiceTypes';

import {
  login as goToLogin,
  previous,
  home,
  coconut,
  account,
  openExternalUrl,
  goToAccountSetupPage,
} from '@dragonfruit/redux/navigation';
import { getPreviousRoute } from '@dragonfruit/redux/location';
import { getLanguage, getRegion } from '@dragonfruit/redux/locale';
import { actions as termsDialogActions } from '@dragonfruit/redux/termsDialog';
import { storeSignupInfo } from '@dragonfruit/redux/signup';
import { sendGtmEvent } from '@dragonfruit/redux/googleTagManager';

import { getQuestionId } from '@dragonfruit/utils/temporaryQuestion';

import { transformOptionFormat } from './transformSelectOption';
// Redirect logic
// Login/
// | ├── check if user confirmed email, if not, show the  verify view at login page.
// | ├── if user's role is tutor,then check the roleStatus.
// |    ├── if tutor hasn't finished the onboarding process, go to account page.
// |    ├── if tutor is approved, go to coconut.
// │ ├── Whitelist true (premium, purchase, change-plan) -> Previous
// │ ├── Whitelist false -> Coconut
const AFTER_LOGIN_COCONUT_REDIRECTION_WHITE_LIST = /(.+\/)?(premium|purchase|change-plan)(\/.+)?/i;

const { ACADEMY_SITE_ENDPOINT } = config;

export function* checkTermsOfService() {
  const termsOfService = yield select(getTermsOfService);
  if (!termsOfService) {
    yield put(termsDialogActions.showTermsDialog());
  }
  return termsOfService;
}

export function* redirectToExternal() {
  const { returnto } = qs.parse(window.location.search);
  if (returnto) {
    yield put(openExternalUrl(returnto));

    return true;
  }

  // referrer has value when other web site redirect to dragonfruit
  if (document.referrer.startsWith(ACADEMY_SITE_ENDPOINT)) {
    yield put(openExternalUrl(document.referrer));

    return true;
  }

  return false;
}

export function* redirectAfterLoggedIn() {
  const role = yield select(getRole);
  const roleStatus = yield select(getRoleStatus);
  const previousRoute = yield select(getPreviousRoute);

  const hasRedirected = yield call(redirectToExternal);

  if (hasRedirected) {
    return;
  }

  const userRegion = yield select(getUserRegion);

  const appRegion = yield select(getRegion);

  const snapCookie = new cookie(COOKIE_DOMAIN);
  const userRegionDefaultLocale = SUPPORTED_REGION_LOCALE_LIST[userRegion];

  // force set the locale by user region for further website navigation
  if (userRegion !== appRegion) {
    snapCookie.setCookie(USER_LNG, userRegionDefaultLocale);
  }

  /*
     1. no_cert -> go to tutor profile page, and show the banner to notice.
     2. under_review, audit_on_hold, cert_expired -> go to tutor profile page
     3. approved -> QA page
     4. rejected -> go to tutor profile page, and explain his application is rejected.
    */

  if (role === TUTOR) {
    if (roleStatus === APPROVED) {
      yield put(coconut());
      return;
    }

    yield put(account());
    return;
  }

  if (AFTER_LOGIN_COCONUT_REDIRECTION_WHITE_LIST.test(previousRoute)) {
    // cause some page have login function, so need go back to previous
    snapCookie.deleteCookie(PREVIOUS_PURCHASE_PAGE);
    yield put(previous());
  } else {
    yield put(goToAccountSetupPage(userRegionDefaultLocale));
  }
}

export function* syncDataWithCookie(data) {
  const userData = Object.assign({}, data);

  const {
    user: {
      firstName,
      lastName,
      snapaskUid,
      externalId: userExternalId,
      countryCode,
      username,
      email,
      profilePicUrl: avatar,
      role: { name: role },
      region: { id: regionId, name: region, bankNeedColumn },
      phone,
      roleStatus,
      unconfirmedEmail,
      termsOfService,
      phoneConfirmedAt,
      schoolName,
    } = {},
    loginType,
    extraQuotaByCompletingProfile,
    extraInfo: { referralQuotaThreshold },
    loveCodes,
    latestInvoiceInfo,
  } = userData;

  const userId = snapaskUid.toString();
  const profileData = {
    firstName,
    lastName,
    countryCode,
    userId,
    userExternalId,
    username,
    email,
    role,
    avatar,
    region,
    regionId,
    loginType,
    phone,
    roleStatus,
    unconfirmedEmail,
    termsOfService,
    phoneConfirmedAt,
    schoolName,
    extraQuotaByCompletingProfile,
    referralQuotaThreshold,
    invoiceLoveCodeList: (loveCodes || []).map(loveCode =>
      transformOptionFormat(loveCode, 'name', 'code')
    ),
    latestInvoiceInfo: latestInvoiceInfo && {
      ...latestInvoiceInfo,
      autoBroughtIn: latestInvoiceInfo.autoBroughtIn || false,
      category: latestInvoiceInfo.category || PERSONAL,
    },
  };

  // remove non-login question
  const snapCookie = new cookie(COOKIE_DOMAIN);
  snapCookie.deleteCookie(QUESTION_ID);
  // save cookie
  snapCookie.saveCookieFromAuthInfo(profileData);
  // if user login from wizmo, the value is true, otherwise should be false
  snapCookie.deleteCookie(IS_WIZMO_USER);

  yield put(fetchBankInfoFieldSuccess({ bankNeedColumn }));
  yield put(storeProfile(profileData));
}

export function* loginSuccessful({ data, token }) {
  yield call(syncDataWithCookie, data);
  yield put(actions.loginSuccess(token));

  const checkTermsResult = yield call(checkTermsOfService);
  if (checkTermsResult) {
    yield call(redirectAfterLoggedIn);
  }

  yield put(fetchSiteInfo());
}

export function* afterLoggedIn({ data, token }) {
  yield call(loginSuccessful, {
    data,
    token,
  });

  yield put(
    showAlertBanner({
      message: 'common:common.welcomeBack',
      theme: ALERT_SUCCESS_THEME,
    })
  );
}

export function* getUserInfoByToken({ payload }) {
  const { token } = payload;

  const snapCookie = new cookie(COOKIE_DOMAIN);
  snapCookie.updateUserToken(token);

  const language = yield select(getLanguage);

  try {
    const { data } = yield call(apiAppLaunch, { lang: language });

    yield call(afterLoggedIn, {
      data,
      token,
    });
  } catch (error) {
    throw error;
  }
}

export function* updateUserInfoByToken() {
  try {
    const language = yield select(getLanguage);
    const { data } = yield call(apiAppLaunch, { lang: language });
    yield call(syncDataWithCookie, data);
    yield call(checkTermsOfService);
    yield put(
      actions.updateUserInfoSuccess({
        canCancelSubscription: !(
          data.uiHideSettings && data.uiHideSettings.cancelSubscriptionButton
        ),
      })
    );
  } catch (error) {
    // user will be redirect to login page
  }
}

export function* oauthLoginFlow({ provider, providerToken, providerUid }) {
  const oauthData = {};

  const questionId = getQuestionId();

  const snapCookie = new cookie(COOKIE_DOMAIN);
  const appsflyerConversion = snapCookie.getAppsFlyerConversionData();

  oauthData.provider = provider;
  oauthData.provider_uid = providerUid;
  oauthData.provider_token = providerToken;

  if (questionId) oauthData.question_id = questionId;
  if (appsflyerConversion)
    oauthData.appsflyer_conversion = JSON.stringify(appsflyerConversion);

  const {
    data: { jwt: token },
  } = yield call(apiOauthLogin, oauthData);

  yield put(actions.getUserInfo(token));
  yield put(
    sendGtmEvent({
      eventName: LOGIN_SUCCESS,
    })
  );
}

export function* loginFlow(action) {
  const {
    payload: { account, password, phoneNumber, countryCode, currentType },
  } = action;

  try {
    // request auth api
    const appsflyerConversion = getAppsflyerFromCookie();

    const loginData = {};

    const questionId = getQuestionId();
    if (questionId) loginData.question_id = questionId;

    switch (currentType) {
      case EMAIL:
        loginData.username_or_email = account;
        loginData.password = password;
        break;
      case FACEBOOK_AUTH_TYPE:
        yield put(actions.facebookLogin());
        break;
      case PHONE:
        loginData.country_code = countryCode;
        loginData.phone = phoneNumber;
        loginData.password = password;
        break;
      default:
        return;
    }

    if (appsflyerConversion)
      loginData.appsflyer_conversion = JSON.stringify(appsflyerConversion);

    const {
      data: {
        jwt: token,
        role: { name },
      },
    } = yield call(apiLogin, loginData);

    // protect admin login, instead of handling by backend.
    if (name === ADMIN) {
      yield put(
        showAlertBanner({
          message: 'error:error.notAllowAdmin',
          theme: ALERT_ERROR_THEME,
        })
      );

      return;
    }
    yield put(actions.getUserInfo(token));
    yield put(
      sendGtmEvent({
        eventName: LOGIN_SUCCESS,
      })
    );
  } catch (err) {
    const { data, message, error_code } = err;
    yield put(actions.loginFailed(message));

    if (error_code === USER_EMAIL_NOT_VERIFIED) {
      const { email } = data;
      yield put(storeSignupInfo({ email }));
      yield put(actions.emailUnverified());
      return;
    }

    yield put(
      showAlertBanner({
        message,
        theme: ALERT_ERROR_THEME,
      })
    );
  }
}

export function* facebookLoginFlow() {
  try {
    const facebookAuthResponse = yield call(getFacebookAuthResponse);
    const { userID, accessToken } = facebookAuthResponse;

    yield call(oauthLoginFlow, {
      provider: 'facebook',
      providerUid: userID,
      providerToken: accessToken,
    });
  } catch (error) {
    yield put(actions.loginFailed(error.message));
    yield put(
      showAlertBanner({
        message: error.message,
        theme: ALERT_ERROR_THEME,
      })
    );
  }
}

function* discardUserAuth() {
  yield put(fetchSiteInfo());

  const snapCookie = new cookie(COOKIE_DOMAIN);
  snapCookie.deleteAuthCookie();

  yield put(resetUser());
}

export function* forceLogoutFlow(action) {
  yield i18n.loadNamespaces(['error', 'common']);

  const { lang, errorCode } = action.payload;
  yield call(discardUserAuth, lang);

  const getModalText = () => {
    switch (errorCode) {
      case TOKEN_AMOUNT_EXCEED:
        return {
          title: i18n.t('common:common.forceLogout.title'),
          description: i18n.t('common:common.deviceAmountExceed'),
        };
      case NOW_TV_SUBSCRIPTION_INVALID:
        return {
          title: i18n.t('error:error.nowtvLogout.title'),
          description: i18n.t('error:error.nowtvLogout.description'),
        };
      default:
        return {
          title: i18n.t('common:common.forceLogout.title'),
          description: i18n.t('common:common.tokenExpired'),
        };
    }
  };

  const modalText = getModalText();

  yield put(
    showAlertModal({
      title: modalText.title,
      description: modalText.description,
      closeButtonText: i18n.t('common:common.forceLogout.closeButtonText'),
    })
  );

  yield put(goToLogin());
}

export function* logoutFlow(action) {
  const { lang } = action.payload;
  try {
    yield call(apiLogout);
    yield call(discardUserAuth, lang);
    yield put(
      showAlertBanner({
        message: 'common:common.logoutSuccess',
        theme: ALERT_SUCCESS_THEME,
      })
    );
    yield put(home());
  } catch (error) {
    yield put(errorActions.sendError(error));
  }
}

export default function*() {
  yield all([
    takeLatest(actions.LOGIN, loginFlow),
    takeLatest(actions.FACEBOOK_LOGIN, facebookLoginFlow),
    takeLatest(actions.FORCE_LOGOUT, forceLogoutFlow),
    takeLatest(actions.LOGOUT, logoutFlow),
    takeLatest(actions.UPDATE_USER_INFO, updateUserInfoByToken),
    takeLatest(actions.GET_USER_INFO, getUserInfoByToken),
  ]);
}



// WEBPACK FOOTER //
// ./app/sagas/auth.js